import { useEffect, useState } from 'react'

import { DateTime } from 'luxon'
import { FiAlertCircle, FiAlertTriangle, FiInfo, FiX } from 'react-icons/fi'
import { MdDoorBack, MdDoorFront, MdSignalWifiConnectedNoInternet0 } from 'react-icons/md'

import GatewayIcon from './AssetTable/GatewayIcon'
import PowerStateIcon from './AssetTable/PowerStateIcon'
import { useMapContext } from './MapContext'
import { AssetStateSummary, Severity } from './types'

const SeverityBadge = ({ severity, label }: { severity: Severity | null; label: string }) => {
  if (!severity) return null

  const colors = {
    critical: 'bg-red-100 text-red-800 border-red-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    informational: 'bg-blue-100 text-blue-800 border-blue-200',
  }

  const icons = {
    critical: <FiAlertCircle className="w-3 h-3" />,
    warning: <FiAlertTriangle className="w-3 h-3" />,
    informational: <FiInfo className="w-3 h-3" />,
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border ${colors[severity]}`}>
      {icons[severity]}
      {label}
    </span>
  )
}

const StatusRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-base-200 last:border-b-0">
    <span className="text-xs text-base-content/70">{label}</span>
    <div className="flex items-center gap-2">{children}</div>
  </div>
)

interface AssetQuickViewProps {
  variant?: 'floating' | 'inline'
  onClose?: () => void
  onShowDetails?: () => void
}

export const AssetQuickView = ({ variant = 'floating', onClose, onShowDetails }: AssetQuickViewProps) => {
  const context = useMapContext()
  const [position, setPosition] = useState({ top: 0, left: 0 })

  // Find the selected asset from filtered assets
  const asset: AssetStateSummary | undefined = context.filteredAssets.find((a) => a.id === context.currentAssetId)

  // Calculate position based on toggle button
  useEffect(() => {
    if (variant !== 'floating') return
    const button = document.getElementById('quick-view-toggle')
    if (button) {
      const rect = button.getBoundingClientRect()
      const mapContainer = button.closest('.flex-1.relative')
      if (mapContainer) {
        const containerRect = mapContainer.getBoundingClientRect()
        setPosition({
          top: rect.bottom - containerRect.top + 4, // 4px gap below button
          left: rect.left - containerRect.left,
        })
      }
    }
  }, [context.currentAssetId, context.mapState.showQuickView])

  if (variant === 'floating' && !context.mapState.showQuickView) return null
  if (!asset) return null

  const handleClose = () => {
    if (onClose) {
      onClose()
    } else {
      context.setMapState({ ...context.mapState, showQuickView: false })
    }
  }

  const handleViewDetails = () => {
    if (onShowDetails) {
      onShowDetails()
    } else {
      context.setAssetDetailSheetIsOpen(true)
    }
  }

  const isSplit = context.sidebarState.layoutMode === 'split'
  const wrapperClassName =
    variant === 'floating'
      ? 'absolute z-40 w-72 bg-base-100 rounded-lg shadow-lg border border-base-300 overflow-hidden'
      : 'bg-base-100 w-full'

  const wrapperStyle =
    variant === 'floating'
      ? isSplit
        ? { bottom: 16, left: position.left }
        : { top: `${position.top}px`, left: `${position.left}px` }
      : undefined
  const hasOfflineDoor = asset.gatewayOn && asset.doorMps.some((d) => !d.tagOnline)
  return (
    <div className={wrapperClassName} style={wrapperStyle}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2  border-b border-base-300">
        <h3 className="font-medium text-base truncate flex-1 pr-2">{asset.name}</h3>
        <button onClick={handleClose} className="btn btn-ghost btn-xs btn-circle" title="Close">
          <FiX className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Status indicators */}
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-base-200">
          <div className="flex items-center gap-1" title={asset.gatewayOn ? 'Gateway Online' : 'Gateway Offline'}>
            <GatewayIcon
              gatewayOn={asset.gatewayOn}
              addressShort={asset.addressShort}
              geofence={asset.geofence}
              umdBatteryVoltage={asset.umdBatteryVoltage}
              umdSampleTime={asset.umdSampleTime}
              umdSleepMode={asset.umdSleepMode}
            />
          </div>
          <div className="flex items-center gap-1" title={asset.powerOn ? 'Power On' : 'Power Off'}>
            <PowerStateIcon
              powerState={asset.powerState}
              powerStateSampleTime={asset.powerStateSampleTime}
              maintenanceModeEventSampleTime={asset.maintenanceModeEventSampleTime}
              maintenanceMode={asset.maintenanceMode}
            />
          </div>

          {asset.doorMps.length > 0 && (
            <div
              className="flex items-center gap-1"
              title={hasOfflineDoor ? 'Door sensor offline' : asset.hasOpenDoor ? 'Door Open' : 'Door Closed'}
            >
              {hasOfflineDoor ? (
                <MdSignalWifiConnectedNoInternet0 />
              ) : asset.hasOpenDoor ? (
                <MdDoorFront className="w-5 h-5 text-orange-500" />
              ) : (
                <MdDoorBack className="w-5 h-5 text-green-500" />
              )}
            </div>
          )}
          {!asset.insideGeofence && <span className="text-xs text-orange-600 font-medium">Outside Geofence</span>}
        </div>

        {/* Severity badges */}
        {(asset.highestSeverity || asset.highestAlertSeverity) && (
          <div className="flex flex-wrap gap-2 mb-3">
            <SeverityBadge severity={asset.highestSeverity} label="Alarm" />
            <SeverityBadge severity={asset.highestAlertSeverity} label="Alert" />
          </div>
        )}

        {/* Details */}
        <div className="space-y-0">
          <div className="text-xs text-base-content/70">Location</div>
          <div className="pl-2 flex items-center gap-2 text-xs">{asset.location.address}</div>

          {asset.activeAlarmCodeIds.length > 0 && (
            <StatusRow label="Active Alarms">
              <span className="text-xs font-medium text-red-600">
                {asset.activeAlarmCodeIds.length} alarm{asset.activeAlarmCodeIds.length !== 1 ? 's' : ''}
              </span>
            </StatusRow>
          )}

          {asset.activeAlertNames.length > 0 && (
            <StatusRow label="Active Alerts">
              <span className="text-xs font-medium text-orange-600">
                {asset.activeAlertNames.length} alert{asset.activeAlertNames.length !== 1 ? 's' : ''}
              </span>
            </StatusRow>
          )}

          <StatusRow label="Last Update">
            <span className="text-xs">{DateTime.fromJSDate(asset.umdSampleTime).toRelative()}</span>
          </StatusRow>
        </div>

        {/* Actions */}
        <div className="mt-3 pt-3 border-t border-base-200">
          <button onClick={handleViewDetails} className="btn btn-primary btn-sm w-full">
            View Full Details
          </button>
        </div>
      </div>
    </div>
  )
}
